import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { TimekeeperService } from 'services/TimekeeperService';
import { DatePatternDaysTable } from './DatePatternDaysTable';

interface ViewDaysDialogProps {
    open: boolean;
    onClose: () => void;
    datePatternId?: number;
    name?: string;
}

const toIsoDate = (date: Date): string => {
    return date.toISOString().slice(0, 10);
};

const plusDays = (baseDate: Date, days: number): Date => {
    const nextDate = new Date(baseDate);
    nextDate.setDate(nextDate.getDate() + days);
    return nextDate;
};

const timekeeperService = new TimekeeperService();

const ViewDaysDialog: React.FC<ViewDaysDialogProps> = ({ open, onClose, datePatternId, name }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [dates, setDates] = useState<string[]>([]);
    const [startDate, setStartDate] = useState(() => toIsoDate(new Date()));
    const [endDate, setEndDate] = useState(() => toIsoDate(plusDays(new Date(), 90)));
    const [limit, setLimit] = useState<number>(100);

    const title = useMemo(() => {
        if (!name) {
            return 'Ver días';
        }

        return `Ver días: ${name}`;
    }, [name]);

    const loadRange = async () => {
        if (!datePatternId) {
            return;
        }

        try {
            setLoading(true);
            const response = await timekeeperService.date_pattern.getRangeById(datePatternId, {
                start_date: startDate || undefined,
                end_date: endDate || undefined,
                limit: Number.isFinite(limit) && limit > 0 ? limit : undefined
            });
            setDates(response);
        } catch (error) {
            console.error('Error al consultar rango de días:', error);
            enqueueSnackbar('No se pudo obtener el listado de días', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open || !datePatternId) {
            return;
        }

        loadRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, datePatternId]);

    const handleClose = () => {
        setDates([]);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent dividers>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                    <TextField
                        label="Desde"
                        type="date"
                        value={startDate}
                        onChange={(event) => setStartDate(event.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                        fullWidth
                    />
                    <TextField
                        label="Hasta"
                        type="date"
                        value={endDate}
                        onChange={(event) => setEndDate(event.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                        fullWidth
                    />
                    <TextField
                        label="Límite"
                        type="number"
                        value={limit}
                        onChange={(event) => setLimit(Number(event.target.value))}
                        slotProps={{ htmlInput: { min: 1 } }}
                        sx={{ minWidth: { sm: 140 } }}
                    />
                    <Button variant="contained" onClick={loadRange} disabled={loading || !datePatternId}>
                        Enviar
                    </Button>
                </Stack>

                <Box>
                    <DatePatternDaysTable
                        dates={dates}
                        loading={loading}
                        emptyMessage="No hay días disponibles para los filtros seleccionados"
                        maxHeight={460}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="error" variant="contained">
                    Cerrar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { ViewDaysDialog };
